import * as THREE from "three";
import {applyAxesToObject, cloneAxes, normalizeDeg} from "./orientationAxes";
import {
    anglesOnSingleAxis,
    composeAxesFromLocalAngles,
    getLocalRotationAxisVector,
    normalizeLocalRotationAngles,
} from "./localRotation";

export const raycastScreenToCameraPlane = (camera, domElement, clientX, clientY, planePointWorld) => {
    const rect = domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndc, camera);
    const camDir = new THREE.Vector3();
    camera.getWorldDirection(camDir);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(camDir, planePointWorld);
    const hit = new THREE.Vector3();
    if (!raycaster.ray.intersectPlane(plane, hit)) {
        return null;
    }
    return hit;
};

/** Направление оси вращения (шарнир X→Y→Z) в СК родителя pose. */
export const getRotationAxisDirInPoseParent = (
    referenceAxes,
    anglesDeg,
    axisKey,
    orientObj,
) => {
    const axisTuple = getLocalRotationAxisVector(referenceAxes, anglesDeg, axisKey);
    const dir = new THREE.Vector3(...axisTuple).normalize();
    const pose = orientObj?.parent;
    if (pose) {
        dir.transformDirection(pose.matrixWorld);
    }
    return dir;
};

export const createFreeRotationDragState = ({
    rotationAxisKey,
    initialAxes,
    referenceAxes,
    startAngles,
    faceFlip,
    orientObj,
    poseObj,
    hitWorld,
}) => {
    const axisTuple = getLocalRotationAxisVector(
        referenceAxes,
        startAngles,
        rotationAxisKey,
    );
    const rotationAxisDir = new THREE.Vector3(...axisTuple).normalize();
    if (poseObj) {
        rotationAxisDir.transformDirection(poseObj.matrixWorld);
    }

    return {
        poseObj,
        orientObj,
        plane: null,
        startHit: hitWorld.clone(),
        rotationAxis: rotationAxisKey,
        rotationAxisDir,
        referenceAxes: cloneAxes(referenceAxes),
        startAngles: normalizeLocalRotationAngles(startAngles),
        startAxes: cloneAxes(initialAxes),
        currentAxes: cloneAxes(initialAxes),
        currentAngles: normalizeLocalRotationAngles(startAngles),
        faceFlip: Boolean(faceFlip),
    };
};

export const applyFreeRotationDragStep = (drag, hitWorld, camera = null) => {
    const axis = drag.rotationAxisDir?.clone?.() ?? drag.rotationAxisDir;
    const orientObj = drag.orientObj;
    const poseObj = drag.poseObj;
    if (!axis || !orientObj || !poseObj) return null;

    const pivot = new THREE.Vector3();
    poseObj.getWorldPosition(pivot);
    const v0 = drag.startHit.clone().sub(pivot).projectOnPlane(axis);
    const v1 = hitWorld.clone().sub(pivot).projectOnPlane(axis);
    if (v0.lengthSq() < 1e-12 || v1.lengthSq() < 1e-12) return null;
    v0.normalize();
    v1.normalize();

    let signedAxis = axis.clone().normalize();
    if (camera) {
        const eye = camera.position.clone().sub(pivot);
        if (eye.lengthSq() > 1e-12 && signedAxis.dot(eye.normalize()) < 0) {
            signedAxis.negate();
        }
    }

    const cross = new THREE.Vector3().crossVectors(v0, v1);
    const angleRad = Math.atan2(signedAxis.dot(cross), v0.dot(v1));
    const deltaDeg = THREE.MathUtils.radToDeg(angleRad);
    const pendingAngles = anglesOnSingleAxis(
        drag.rotationAxis,
        normalizeDeg((drag.startAngles[drag.rotationAxis] || 0) + deltaDeg),
    );
    const nextAxes = composeAxesFromLocalAngles(drag.referenceAxes, pendingAngles);
    drag.currentAxes = nextAxes;
    drag.currentAngles = pendingAngles;
    applyAxesToObject(orientObj, nextAxes, drag.faceFlip);
    return {axes: nextAxes, localRotationAngles: pendingAngles};
};
